import {FormValues} from "./form";
import {DragHandleIcon} from "@/components/widgets";
import {Checkbox, IconButton, Tooltip} from "@mui/material";
import {formCheckboxMargin} from "@/constants";
import {DeleteIcon} from "@/components/icons";
import {useStyles} from "./styles";
import {useIntl} from "react-intl";
import {useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";

type MemberRowProps = {
    enabled: boolean;
    member: FormValues["members"][number];

    onDeleteMember: (numberId: number) => void;
    onOverrideNumberChanged: (numberId: number, overrideNumber: boolean) => void;
    onSupervisorChanged: (numberId: number, controllingLine: boolean) => void;
    onAllowEditAddressBookChanged: (numberId: number, allowEditAddressBook: boolean) => void;

    canBeSupervisor: boolean;

    isCallQueue: boolean;
    groupHasAddressBook: boolean;
};

export const MemberRow = ({
    enabled,
    member,
    onDeleteMember,
    onOverrideNumberChanged,
    onSupervisorChanged,
    onAllowEditAddressBookChanged,
    canBeSupervisor,
    isCallQueue,
    groupHasAddressBook,
}: MemberRowProps) => {
    const {attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition} = useSortable({
        id: member.numberId.toString(),
    });

    const {classes} = useStyles();

    const draggableStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <tr className={classes.row} ref={setNodeRef} style={draggableStyle}>
            <td>
                <DragHandleIcon
                    setActivatorNodeRef={setActivatorNodeRef}
                    listeners={listeners}
                    attributes={attributes}
                />
            </td>
            <td>{member.shortNumber}</td>
            <td>{member.displayName}</td>
            <td>
                <Checkbox
                    disabled={!enabled}
                    checked={member.overrideNumber ?? false}
                    onChange={(_, checked) => onOverrideNumberChanged(member.numberId, checked)}
                    color="primary"
                    style={{marginLeft: formCheckboxMargin}}
                />
            </td>
            {isCallQueue && (
                <>
                    <td>
                        <SupervisorCheckbox
                            disabled={!enabled}
                            canBeSupervisor={canBeSupervisor}
                            checked={member.controllingLine}
                            onChange={(checked) => onSupervisorChanged(member.numberId, checked)}
                        />
                    </td>
                    {groupHasAddressBook && (
                        <td>
                            <Checkbox
                                disabled={!enabled}
                                checked={member.allowEditAddressBook}
                                onChange={(_, checked) => onAllowEditAddressBookChanged(member.numberId, checked)}
                                color="primary"
                            />
                        </td>
                    )}
                </>
            )}
            <td className={classes.actionCell}>
                <IconButton
                    color="secondary"
                    size="small"
                    onClick={() => onDeleteMember(member.numberId)}
                    disabled={!enabled}
                >
                    <DeleteIcon />
                </IconButton>
            </td>
        </tr>
    );
};

type SupervisorCheckboxProps = {
    disabled: boolean;
    canBeSupervisor: boolean;
    checked: boolean;
    onChange: (checked: boolean) => void;
};

const SupervisorCheckbox = ({disabled, canBeSupervisor, checked, onChange}: SupervisorCheckboxProps) => {
    const intl = useIntl();

    const CheckboxWithoutTooltip = () => {
        return (
            <Checkbox
                disabled={disabled || !canBeSupervisor}
                checked={checked}
                onChange={(_, checked) => onChange(checked)}
                color="primary"
                style={{padding: 0, paddingLeft: "9px"}} // 9px MUI default
            />
        );
    };
    if (canBeSupervisor) {
        return <CheckboxWithoutTooltip />;
    } else {
        return (
            <div>
                <Tooltip
                    title={intl.formatMessage({
                        id: "call_group_memberships.no_supervisor",
                        defaultMessage: "Can not be set if the user does not have Supervisor capability.",
                    })}
                >
                    <div style={{width: "fit-content"}}>
                        <CheckboxWithoutTooltip />
                    </div>
                </Tooltip>
            </div>
        );
    }
};
